import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared layout for static legal/policy pages (/terms, /refund, /privacy).
 * Matches the homepage white-canvas + green-accent typography rather than
 * the dark admin theme — these pages must be reachable from search engines
 * and customer signup flows without breaking visual continuity.
 */
export default function PolicyLayout({ title, eyebrow, effectiveDate, children }) {
  const [branding, setBranding] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/branding`).then((r) => setBranding(r.data)).catch(() => {});
    document.title = `${title} · Rescigo Health`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="policy-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={branding?.logo_url || "/brand/rescigo-logo.png"}
                 alt="Rescigo Health" className="h-8 w-auto" data-testid="policy-logo" />
          </Link>
          <Link to="/" data-testid="policy-back"
                className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition">
            <ArrowLeft size={11} weight="bold" /> Back to website
          </Link>
        </div>
      </header>

      {/* Decorative hero strip */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-[#F7FAF6] to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#2D5A27]/15 text-[10px] uppercase tracking-[0.24em] text-[#1B3B17] font-semibold">
            <ShieldCheck size={11} weight="fill" color="#2D5A27" /> {eyebrow || "Legal"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium text-slate-900 mt-5">
            {title}
          </h1>
          {effectiveDate && (
            <div className="text-xs text-slate-500 mt-3 font-mono">{effectiveDate}</div>
          )}
        </div>
      </section>

      <article data-testid="policy-content"
               className="max-w-3xl mx-auto px-6 lg:px-8 py-12 lg:py-16 prose prose-slate prose-headings:font-heading prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h2:font-medium prose-p:leading-relaxed prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-900 prose-a:text-[#2D5A27] prose-a:no-underline hover:prose-a:underline">
        {children}
      </article>

      {/* Cross-links footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium mb-4">Related policies</div>
          <div className="flex flex-wrap gap-2">
            <CrossLink to="/terms" label="Terms & Conditions" />
            <CrossLink to="/refund" label="Cancellation & Refund" />
            <CrossLink to="/privacy" label="Privacy Policy" />
            <CrossLink to="/#contact" label="Contact us" />
          </div>
          <div className="text-[11px] text-slate-500 mt-6">
            © {new Date().getFullYear()} React Disaster Management Solutions OPC Pvt. Ltd. · All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

const CrossLink = ({ to, label }) => (
  <Link to={to} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs text-slate-700 hover:border-[#2D5A27] hover:text-[#1B3B17] transition">
    {label}
  </Link>
);
